// @flow

import CartStorage from "../helper/storage/CartStorage";
import type {Item} from "../models/Item";
import {getMaxOrderableQuantity} from "../models/Product";
import type {PaymentFacility, PaymentMethod} from "../api/PaymentApi";
import {createSlice} from "@reduxjs/toolkit";
import {configSet} from "./configReducer";
import {cartFailureChecked, orderDetailsCleared} from "./orderReducer";

export type CartReducerState = {
	dealerId: ?string,
	items: Item[],
	isDrawerOpen: boolean,
	isLoading: boolean,
	isAssigningPayment: boolean,
	paymentFacility: PaymentFacility,
	paymentMethods: PaymentMethod[],
	paypalClientId: string,
	assignPaymentErrors: {},
	paymentApiError: boolean,
};

const initialState = {
	dealerId: null,
	items: [],
	isDrawerOpen: false,
	isLoading: false,
	isAssigningPayment: false,
	paymentFacility: null,
	paymentMethods: [],
	paypalClientId: null,
	assignPaymentErrors: {},
	paymentApiError: false,
};

const setCart = (cart, state) => {
	const cartStorage = new CartStorage(state.dealerId);
	cartStorage.set(JSON.stringify(cart));
};

const cartSlice = createSlice({
	name: "cart",
	initialState,
	reducers: {
		cartDrawerOpened(state) {
			state.isDrawerOpen = true;
		},
		cartDrawerClosed(state) {
			state.isDrawerOpen = false;
		},
        paypalClientIdSet(state, action) {
            state.paypalClientId = action.payload;
        },
		paymentMethodsRequestGotten(state) {
			state.paymentMethods = [];
			state.paymentFacility = {};
			state.isLoading = true;
			state.paymentApiError = false;
		},
		paymentMethodsSuccessGotten(state, action) {
			state.paymentMethods = action.payload.paymentMethods;
			state.paypalClientId = action.payload.clientId;
			state.isLoading = false;
		},
		paymentMethodsFailureGotten(state) {
			state.paymentMethods = [];
			state.isLoading = false;
			state.paymentApiError = true;
		},
		// a payment method chosen on the overview: the page stays, only its pay button waits
		paymentProviderRequestSet(state) {
			state.paymentFacility = {};
            state.paymentApiError = false;
			state.isAssigningPayment = true;
		},
		paymentProviderSuccessSet(state, action) {
            state.paymentFacility = action.payload;
			state.isAssigningPayment = false;
			state.isLoading = false;
		},
        paymentProviderReset(state, _action) {
            state.paymentFacility = initialState.paymentFacility;
            state.isAssigningPayment = false;
            state.isLoading = false;
        },
		paymentProviderFailureSet(state, action) {
			const errors = state.assignPaymentErrors;
			action.payload.paymentMethods.forEach(paymentMethod => {
				errors[paymentMethod] = true;
			});

			state.assignPaymentErrors = {...errors};
			state.isAssigningPayment = false;
			state.isLoading = false;
		},
		createOrderRequested(state) {
			state.assignPaymentErrors = {};
			state.paymentApiError = false;
            state.isLoading = true;
		},
		createOrderFailed(state) {
			state.isLoading = false;
			state.paymentApiError = true;
            state.isLoading = false;
		},
		verifyPaymentFailed(state) {
			state.isLoading = false;
			state.paymentApiError = true;
		},
		toCartAdded(state, action) {
			let itemFound = false;
			state.items = state.items.map(item => {
				if (action.payload.product.id === item.product.id) {
					// never exceed the available stock (v1: single item = 1); unlimited products are uncapped
					item.amount = Math.min(item.amount + 1, getMaxOrderableQuantity(item.product));
					itemFound = true;
				}
				return item;
			});
			if (itemFound === false) {
				state.items.push({
					product: action.payload.product,
					amount: 1,
					shippingMethod: action.payload.shippingMethod
				});
			}

			setCart({items: [...state.items]}, state);
			state.items = [...state.items];

		},
		fromCartRemoved(state, action) {
			const items = state.items.filter(item => item.product.id !== action.payload.id);
			setCart({items}, state);
			state.items = items;
		},
		allFromCartRemoved(state) {
			setCart({items: []}, state);
			state.items = [];
		},
		quantitySet(state, action) {
			state.items = state.items.map(item => {
				if (action.payload.id === item.product.id) {
					item.amount = action.payload.amount;
					item.amount = (item.amount < 1) ? 1 : item.amount;
					// clamp to available stock (v1: single item = 1); unlimited products are uncapped
					item.amount = Math.min(item.amount, getMaxOrderableQuantity(item.product));
				}
				return item;
			});
			setCart({items: [...state.items]}, state);
		},
		itemMarkedSoldOut(state, action) {
			// flag the affected item unavailable so the cart can prompt its removal (order was rejected as sold out)
			state.items = state.items.map(item => {
				if (item.product.id === action.payload.productNo) {
					item.product = {...item.product, isAvailable: false};
				}
				return item;
			});
			setCart({items: [...state.items]}, state);
			state.items = [...state.items];
		}

	},
	extraReducers: builder => {
		builder
			.addCase(configSet, (state, action) => {
				state.dealerId = action.payload.dealerId;
				state.items = action.payload.cart.items;
			})
			.addCase(orderDetailsCleared, state => {
				setCart({items: []}, state);
				state.items = [];
			})
			// the cart no longer holds as it is (a price changed, an item went): the cart shows why
			.addCase(cartFailureChecked, state => {
				state.isDrawerOpen = true;
			});
	}
});

export const {
	cartDrawerOpened,
	cartDrawerClosed,

	toCartAdded,
	fromCartRemoved,
	allFromCartRemoved,
    quantitySet,
    itemMarkedSoldOut,

    paypalClientIdSet,

	paymentMethodsRequestGotten,
	paymentMethodsSuccessGotten,
    paymentMethodsFailureGotten,

    paymentProviderRequestSet,
	paymentProviderSuccessSet,
    paymentProviderFailureSet,
    paymentProviderReset,

	verifyPaymentFailed,

	createOrderRequested,
	createOrderFailed,

} = cartSlice.actions;

export default cartSlice.reducer;
